import { Injectable } from '@angular/core'
import {
  PeriodLength,
  RootState,
  selectActualReconDate,
} from '@helios/state-management'
import { ModalPositionService } from '@helios/user-preferences'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { filter, take } from 'rxjs/operators'
import {
  CreateNewSessionModalComponent,
  CreateNewSessionModalResponse,
} from './create-new-session-modal.component'

/** Service for showing and hiding the create new session modal. */
@Injectable({
  providedIn: 'root',
})
export class CreateNewSessionModalService {
  /** @internal */
  _cachedCreateNewSessionModalInputs: {
    reconDate?: Date
    periodLength: PeriodLength
    periodLengthNDays?: number
    fromDate?: Date
  } = {
    periodLength: PeriodLength.Last100Days,
  }

  constructor(
    private readonly modalService: NgbModal,
    private readonly store: Store<RootState>,
    readonly modalPositionService: ModalPositionService,
  ) {
    this.store
      .select(selectActualReconDate)
      .pipe(
        filter((val): val is Date => val != null),
        take(1),
      )
      .toPromise()
      .then(date => {
        if (this._cachedCreateNewSessionModalInputs.reconDate == null)
          this._cachedCreateNewSessionModalInputs.reconDate = date
      })
  }

  /** Show/Hide the create new session modal. */
  toggleCreateNewSessionModal(
    accountIds: number[],
    reconDate?: Date,
    hasCustomFromDate: boolean = false,
    isLIBAccount: boolean = false,
  ): Promise<CreateNewSessionModalResponse | undefined> {
    const compKey = 'create-new-session'
    const uniqueId = compKey + '-' + Date.now()
    const modalRef = this.modalService.open(CreateNewSessionModalComponent, {
      keyboard: false,
      windowClass: uniqueId,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          compKey,
        )
      },
    })
    const component =
      modalRef.componentInstance as CreateNewSessionModalComponent

    component.accountIds?.setValue(accountIds.join(', '))
    component.reconDate?.setValue(
      reconDate ?? this._cachedCreateNewSessionModalInputs.reconDate,
    )
    if (hasCustomFromDate) {
      component.periodLength?.setValue(PeriodLength.FromDate)
    } else {
      component.periodLength?.setValue(
        this._cachedCreateNewSessionModalInputs.periodLength,
      )
    }
    component.fromDate?.setValue(
      this._cachedCreateNewSessionModalInputs.fromDate,
    )
    component.isLIBAccount = isLIBAccount

    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)

    return modalRef.result.then(
      response => {
        this._cachedCreateNewSessionModalInputs = {
          ...response,
        }
        return response
      },
      () => {},
    )
  }
}
